import { createContext, useCallback, useContext, useMemo, useRef } from "react"
import {
  FilterIds,
  FilterQueryArgs,
  GraphType,
  useFilterQueryParams,
} from "../../hooks/filter_query_params"
import { ToolTipPropType } from "@ciq/shared/components/modal"
import { PopUpPosition } from "@ciq/shared/components/tooltip"
import { formatSnakeCase } from "@ciq/shared/utils/format"
import { getFloorModalKey, getRoomModalKey } from "@ciq/shared/utils/labelHelpers"
import { useMapViewData } from "../../hooks/map_view_data"
import { isEmptyObject } from "@ciq/shared/utils/object"
import { buildSelectorData } from "../../utils/selectorUtils"
import { useFlowDetails } from "./useFlowDetails"

const RightPaneDataContext = createContext()

export function RightPaneDataProvider({ children }) {
  const data = RightPaneData()
  return <RightPaneDataContext.Provider value={data}>{children}</RightPaneDataContext.Provider>
}

export function useRightPaneData() {
  const context = useContext(RightPaneDataContext)
  if (!context) {
    throw new Error("useRightPaneData must be used within an RightPaneDataProvider")
  }
  return context
}

const RightPaneData = () => {
  const zoneData = useZoneData()
  const semesterData = useSemesterData()
  const hourlyData = useHourlyData()
  const flowDetails = useFlowDetails()
  const { setResourceIdAndCampusFlowBuilding } = useFilterQueryParams()

  const handleFlowClose = useCallback(() => {
    setResourceIdAndCampusFlowBuilding(null, null)
  }, [setResourceIdAndCampusFlowBuilding])

  return {
    zoneData,
    semesterData,
    hourlyData,
    flowDetails,
    handleFlowClose,
  }
}

const useZoneData = () => {
  const { resources } = useMapViewData()
  const { zone, getUsage, getBuilding } = resources

  const { primaryUsage, secondaryUsage, spaceCode } = useMemo(() => {
    const primaryUsage = getUsage(zone?.primaryUsageId)
    const secondaryUsage = getUsage(zone?.secondaryUsageId)

    let spaceCode = null
    if (primaryUsage?.code) {
      spaceCode = primaryUsage.code
    } else if (secondaryUsage?.code) {
      spaceCode = secondaryUsage.code
    }

    return {
      primaryUsage: primaryUsage?.name,
      secondaryUsage: secondaryUsage?.name,
      spaceCode,
    }
  }, [getUsage, zone?.primaryUsageId, zone?.secondaryUsageId])

  const operatingHours = useMemo(() => {
    const building = getBuilding(zone?.buildingId)
    if (!building?.operatingHours?.length) {
      return null
    }

    return building.operatingHours
  }, [getBuilding, zone?.buildingId])

  // Helper for numeric-aware sorting
  const sortMixed = arr => arr?.slice()?.sort((a, b) => {
    const aNum = Number(a)
    const bNum = Number(b)
    if (isFinite(aNum) && isFinite(bNum)) return aNum - bNum
    return String(a).localeCompare(String(b))
  })

  const floorLabel = getFloorModalKey(zone?.floors)
  const roomLabel = getRoomModalKey(zone?.rooms)

  return {
    isVisible: zone != null,
    zone,
    primaryUsage,
    secondaryUsage,
    spaceCode,
    floors: sortMixed(zone?.floors)?.join(", "),
    rooms: sortMixed(zone?.rooms) || [],
    floorLabel,
    roomLabel,
    operatingHours,
    squareFootage: zone?.squareFootage,
    capacity: zone?.capacity,
  }
}

const useSemesterData = () => {
  const { resources, semesterSummary } = useMapViewData()
  const { zone } = resources
  const { metric, table, zoneData: displayData } = semesterSummary

  const { mapDisplayType, setFilters } = useFilterQueryParams()

  // ignore for dep arrays
  const setFiltersRef = useRef(setFilters)

  const selector = useMemo(() => {
    return {
      label: "Semester Metric",
      tooltip: {
        type: ToolTipPropType.Info,
        view: "Semester Summary Metric",
        position: PopUpPosition.Top,
      },
      ...buildSelectorData(metric, table, FilterQueryArgs.SEMESTER_METRIC, setFiltersRef.current),
    }
  }, [metric, table])

  return {
    isHeaderVisible: mapDisplayType === FilterIds.Options.SEMESTER,
    isSummaryVisible: zone != null,
    selector,
    displayData,
  }
}

const useHourlyData = () => {
  const { resources, hourlySummary } = useMapViewData()
  const { zone } = resources
  const { rawDayGraphData, rawHourGraphData, metric, defaultHourlyGraphMetric, table } =
    hourlySummary

  const { days, graphType, mapDisplayType, setFilters } = useFilterQueryParams()

  // ignore for dep arrays
  const setFiltersRef = useRef(setFilters)

  const selector = useMemo(() => {
    return {
      label: "Hourly Metric",
      tooltip: {
        type: ToolTipPropType.Info,
        view: "Hourly Summary Metric",
        position: PopUpPosition.Top,
      },
      ...buildSelectorData(
        metric,
        table,
        FilterQueryArgs.UTILIZATION_METRIC,
        setFiltersRef.current
      ),
    }
  }, [metric, table])

  const legendData = useMemo(() => {
    return (graphType === GraphType.BY_DAY ? rawDayGraphData : rawHourGraphData)?.legend
  }, [graphType, rawDayGraphData, rawHourGraphData])

  let graphData = null
  if (zone?.id) {
    const values = Object.values(GraphType).map(t => ({
      selected: t === graphType,
      label: formatSnakeCase(t),
      value: t,
    }))

    graphData = {
      hasData: rawDayGraphData != null || rawHourGraphData != null,
      pill: {
        label: null,
        values,
        onChange: newType => {
          if (graphType === newType.value) {
            return
          }

          setFiltersRef.current({ [FilterQueryArgs.GRAPH_TYPE]: newType.value })
        },
      },
      graph: {
        dayGraph: {
          show: graphType === GraphType.BY_DAY,
          hasData: rawDayGraphData != null,
          datasource: {
            getYLabels: () => rawDayGraphData?.labels.y,
            getXLabels: () => rawDayGraphData?.labels.x,
            getCapacity: () => rawDayGraphData?.capacity,
            getColorInfoForMetric: (day, hour) => {
              const value = rawDayGraphData?.get(day, hour)
              if (isEmptyObject(value)) {
                return defaultHourlyGraphMetric
              }

              return {
                ...value,
                opacity: days == null || days.includes(day) ? 1 : 0.2,
              }
            },
          },
        },
        hourGraph: {
          show: graphType === GraphType.BY_HOUR,
          hasData: rawHourGraphData != null,
          datasource: {
            getYLabels: () => rawHourGraphData?.labels.y,
            getXLabels: () => rawHourGraphData?.labels.x,
            getCapacity: () => rawHourGraphData?.capacity,
            getColorInfoForMetric: hour => {
              const value = rawHourGraphData?.get(hour)
              if (isEmptyObject(value)) {
                return defaultHourlyGraphMetric
              }

              return value
            },
          },
        },
        legend: {
          show: !!legendData?.show,
          numberOfLegendValues: () => {
            return legendData?.count
          },
          getLegendAnchorText: () => {
            return legendData?.anchor
          },
          getColorInfoForIndex: index => {
            return legendData?.get(index)
          },
        },
      },
    }
  }

  return {
    isHeaderVisible: mapDisplayType === FilterIds.Options.HOURLY,
    isSummaryVisible: zone != null,
    selector,
    graphData,
  }
}
